// Google Analytics 4 (gtag.js) — BASIC consent mode.
//
// Nothing Google-related is loaded, initialised or configured unless the user
// has accepted analytics (stored choice) or clicks "Accept All". No cookieless
// pings are sent before consent. Withdrawing consent disables GA, stops future
// hits and removes the GA cookies our code can reach.

const GA_MEASUREMENT_ID = "G-6WEMGTYL05";
const CONSENT_KEY = "cookie-consent";
let loaded = false;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

type StoredConsent = { essential: boolean; analytics: boolean; timestamp?: string; expiresAt: string };

/** Valid, unexpired stored choice, or null. Expired/invalid choices are cleared. */
export const getStoredConsent = (): StoredConsent | null => {
  let raw: string | null = null;
  try { raw = localStorage.getItem(CONSENT_KEY); } catch { return null; }
  if (!raw) return null;
  let parsed: StoredConsent | null = null;
  try { parsed = JSON.parse(raw); } catch { parsed = null; }
  const exp = parsed?.expiresAt ? new Date(parsed.expiresAt).getTime() : NaN;
  if (!parsed || typeof parsed.analytics !== "boolean" || !Number.isFinite(exp) || exp < Date.now()) {
    try { localStorage.removeItem(CONSENT_KEY); } catch { /* ignore */ }
    try { deleteGaCookies(); } catch { /* ignore */ }
    return null;
  }
  return parsed;
};

export const hasAnalyticsConsent = (): boolean => getStoredConsent()?.analytics === true;

/** Loads GA4 only when consent is stored. Safe to call repeatedly. */
export const initGA = () => {
  if (typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  if (loaded) return;
  loaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  const script = document.createElement("script");
  script.async = true;
  script.id = "ga4-gtag";
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

/** Called after "Accept All" (the choice must already be stored). */
export const grantAnalyticsConsent = () => {
  initGA();
};

const deleteGaCookies = () => {
  const names = document.cookie
    .split(";")
    .map((c) => c.split("=")[0].trim())
    .filter((n) => n === "_ga" || n.startsWith("_ga_") || n === "_gid" || n === "_gat" || n.startsWith("_gat_"));
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = [""];
  for (let i = 0; i < parts.length - 1; i++) domains.push(`; domain=.${parts.slice(i).join(".")}`);
  for (const name of names) {
    for (const d of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d}`;
    }
  }
};

/** Called after "Reject All" / withdrawal. Stops hits and clears GA cookies. */
export const revokeAnalyticsConsent = () => {
  if (typeof window === "undefined") return;
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  if (loaded && window.gtag) {
    window.gtag("consent", "update", { analytics_storage: "denied" });
  }
  try { deleteGaCookies(); } catch { /* ignore */ }
};

/** Store the user's choice and apply it immediately. */
export const setAnalyticsChoice = (analytics: boolean) => {
  const consent = {
    essential: true,
    analytics,
    timestamp: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
  };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(consent)); } catch { /* ignore */ }
  if (analytics) grantAnalyticsConsent(); else revokeAnalyticsConsent();
};
